// src/pages/Reports.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./table.css";
import { authHeaders } from "../utils/authHeaders";
import ProcurementRegister from "./ProcurementRegister";
import RecordedBags from "./RecordedBags";

const FY_OPTIONS = [
  { id: "FY25-26", label: "FY 2025-26 (Apr–Mar)" },
  { id: "FY26-27", label: "FY 2026-27 (Apr–Mar)" },
];

const API_BASE = process.env.REACT_APP_API_BASE;

export default function Reports() {
  const isFpoAdmin = localStorage.getItem("role") === "fpo_admin";
  // ===== P3-B Step 1: table + pagination wiring (UI first) =====
  const PAGE_SIZES = [50, 100, 200, 500];

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]); // [{ key, label }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const invoiceActionCellStyle = {
    width: 200, minWidth: 200, overflow: "visible",
    textOverflow: "clip", whiteSpace: "nowrap",
  };

  function clampPage(p) {
    if (p < 1) return 1;
    if (p > totalPages) return totalPages;
    return p;
  }

  const tabs = useMemo(
    () => [
      {
        key: "sales",
        label: "Sales",
        reports: [
          { key: "sales_register", label: "Sales Register (Invoice-wise)" },
          { key: "sales_summary", label: "Sales Summary (Daily/Weekly/Monthly)" },
          { key: "product_sales", label: "Product-wise Sales (SKU-wise)" },
          { key: "agent_sales", label: "Agent-wise Sales" },
          { key: "customer_sales", label: "Customer-wise Sales" },
        ],
      },
      {
        key: "payments",
        label: "Payments & Dues",
        reports: [
          { key: "dues_register", label: "Dues Register (Dues Register)" },
          { key: "receipt_register", label: "Receipt Register (Receipt-wise)" },
          { key: "receipt_alloc", label: "Receipt → Invoice Allocation" },
          { key: "due_aging", label: "Due Aging (Buckets)" },
          { key: "collection_summary", label: "Collection Summary" },
        ],
      },
      {
        key: "inventory",
        label: "Inventory & Stock",
        reports: [
          { key: "inventory_ledger_summary", label: "Inventory Ledger Summary" },
          { key: "agent_stock_availability", label: "Agent Stock Availability" }, // ✅ added (ignore if already there)
          { key: "stock_availability", label: "Stock Availability (SKU-wise)" },
          { key: "stock_movement", label: "Agent Stock Movement (Ledger)" },
          { key: "expiry_tracker", label: "Expiry Tracker (Batch-wise)" },
        ],
      },
      {
        key: "agents",
        label: "Agents",
        reports: [
          { key: "agent_income_margin", label: "Agent Income & Margin" },
          { key: "agent_aging", label: "Agent Aging / Recovery" },
          { key: "top_performers_rolling", label: "Top Performers (Rolling)" },
        ],
      },
      {
        key: "geo",
        label: "Geo & Households",
        reports: [
          { key: "hh_summary", label: "Household Onboarding Summary" },
          { key: "fpo_mapped_geography", label: "FPO Mapped Geography" },
          { key: "geo_drill", label: "Geo Drill Summary" },
          { key: "customer_base_geo", label: "Customer Base by Geo" },
        ],
      },
      {
        key: "products",
        label: "Products",
        reports: [
          { key: "sku_master", label: "SKU Master Report" },
          { key: "sku_cost", label: "SKU Cost (Weighted Avg)" },
          { key: "sku_margin", label: "SKU Margin Report" },
          { key: "near_expiry", label: "Near-Expiry SKU Report" },
        ],
      },
      ...(isFpoAdmin ? [{ key: "procurement", label: "Procurement", reports: [{ key: "procurement_register", label: "Procurement Register" }, { key: "recorded_bags", label: "Recorded Bags Used" }] }] : []),
    ],
    [isFpoAdmin]
  );

  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const activeTabObj = tabs.find((t) => t.key === activeTab) || tabs[0];

  const [activeReport, setActiveReport] = useState(activeTabObj.reports[0].key);
  const isProcurementRegister = isFpoAdmin && activeTab === "procurement" && activeReport === "procurement_register";
  const isRecordedBags = isFpoAdmin && activeTab === "procurement" && activeReport === "recorded_bags";
  const isProcurementReport = isProcurementRegister || isRecordedBags;

  // Filters (UI only in P1)
  const [period, setPeriod] = useState("daily"); // daily|weekly|monthly
  const [fy, setFy] = useState(FY_OPTIONS[0].id);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Optional Geo filters (kept optional)
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [block, setBlock] = useState("");
  const [panchayat, setPanchayat] = useState("");

  const [searchText] = useState("");

  // Reports
  const isSalesRegister =
    activeTab === "sales" && activeReport === "sales_register";
  const isAgentInvoices =
    activeTab === "sales" && activeReport === "agent_sales";
  const isAgentIncomeMargin = activeReport === "agent_income_margin";

  const isDuesRegister =
    activeTab === "payments" && activeReport === "dues_register";
  const isStockAvailability =
    activeTab === "inventory" && activeReport === "stock_availability";
  const isInventoryLedgerSummary =
    activeTab === "inventory" &&
    activeReport === "inventory_ledger_summary";
  const isAgentStockAvailability =
    activeReport === "agent_stock_availability"; // ✅
  const isExpiryTracker = activeReport === "expiry_tracker";
  const isHouseholdSummary =
    activeTab === "geo" && activeReport === "hh_summary";
  const isFpoMappedGeography =
    activeTab === "geo" && activeReport === "fpo_mapped_geography";

  // ✅ (2) Add state + flag
  const isTopPerformersRolling =
    activeReport === "top_performers_rolling";
  const [rollingDays, setRollingDays] = React.useState("7"); // "7"|"14"|"30"
  const [topSort, setTopSort] = React.useState("sales"); // sales|paid|margin|score

  // ✅ totals state (added near others)
  const [, setReportTotals] = React.useState(null);

  // ✅ State + flags (added)
  const [agentFilter, setAgentFilter] = React.useState("");
  const [includeZero, setIncludeZero] = React.useState(false);
  const [showZero, setShowZero] = React.useState(false);
  const [selectedAgentId, setSelectedAgentId] = React.useState("");
  const [hhIsShareholder, setHhIsShareholder] = React.useState("");
  const [hhIsActive, setHhIsActive] = React.useState("");
  const [village, setVillage] = React.useState("");

  const [invoicePreview, setInvoicePreview] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  const invoiceRequest = useRef(null);
  const invoiceUrls = useRef(new Set());
  const currentInvoice = useRef(null);

  function releaseInvoiceUrl(url) {
    if (invoiceUrls.current.delete(url)) window.URL.revokeObjectURL(url);
  }

  function closeInvoice() {
    if (currentInvoice.current) releaseInvoiceUrl(currentInvoice.current.url);
    currentInvoice.current = null;
    setInvoicePreview(null);
  }

  useEffect(() => () => {
    invoiceRequest.current?.abort();
    for (const url of invoiceUrls.current) window.URL.revokeObjectURL(url);
    invoiceUrls.current.clear();
  }, []);

  function downloadInvoiceDocument(document) {
    const anchor = window.document.createElement("a");
    anchor.href = document.url;
    anchor.download = document.filename;
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  async function openInvoice(row, download = false) {
    if (invoiceRequest.current) return;
    if (download && currentInvoice.current?.saleId === row.sale_id) {
      downloadInvoiceDocument(currentInvoice.current);
      return;
    }
    closeInvoice();
    setInvoiceError("");
    setInvoiceLoading(true);
    const controller = new AbortController();
    invoiceRequest.current = controller;
    try {
      const response = await fetch(`${API_BASE}/sales/${encodeURIComponent(row.sale_id)}/invoice-pdf`, {
        headers: authHeaders(), signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Unable to load invoice (HTTP ${response.status}). Please try again.`);
      if (!(response.headers.get("Content-Type") || "").toLowerCase().includes("application/pdf")) {
        throw new Error("The server did not return an invoice PDF. Please try again.");
      }
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      const url = window.URL.createObjectURL(blob);
      invoiceUrls.current.add(url);
      const disposition = response.headers.get("Content-Disposition");
      const filename = disposition?.match(/filename="?([^";]+)"?/i)?.[1] || `sale-${row.sale_id}.pdf`;
      const document = { saleId: row.sale_id, url, filename };
      if (download) {
        try { downloadInvoiceDocument(document); }
        finally { setTimeout(() => releaseInvoiceUrl(url), 0); }
      } else {
        currentInvoice.current = document;
        setInvoicePreview(document);
      }
    } catch (error) {
      if (!controller.signal.aborted) setInvoiceError(error.message || "Unable to load invoice. Please try again.");
    } finally {
      if (invoiceRequest.current === controller) invoiceRequest.current = null;
      if (!controller.signal.aborted) setInvoiceLoading(false);
    }
  }

  async function downloadCsvWithAuth(url, defaultFilename) {
    const res = await fetch(url, {
      headers: authHeaders(),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      alert(text || `CSV download failed: ${res.status}`);
      return;
    }

    const blob = await res.blob();

    let filename = defaultFilename || "report.csv";

    const disposition = res.headers.get("Content-Disposition");
    const match = disposition && disposition.match(/filename="?([^"]+)"?/i);

    if (match && match[1]) {
      filename = match[1];
    }

    const objectUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(objectUrl);
  }

  async function downloadSalesRegisterCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      period,
      fy,
      from: fromDate || "",
      to: toDate || "",
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/sales-register?${params.toString()}`,
      "sales_register.csv"
    );
  }

  async function downloadExpiryTrackerCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      export: "csv",
      agent_id: selectedAgentId || "",
      show_zero: showZero ? "1" : "0",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/expiry-tracker?${params.toString()}`,
      "expiry_tracker.csv"
    );
  }

  async function downloadAgentIncomeMarginCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      period,
      fy,
      from: fromDate || "",
      to: toDate || "",
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/agent-income?${params.toString()}`,
      "agent_income_margin.csv"
    );
  }

  async function downloadStockAvailabilityCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      q: searchText || "",
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/stock-availability?${params.toString()}`,
      "stock_availability.csv"
    );
  }

  async function downloadDuesRegisterCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      period,
      fy,
      from: fromDate || "",
      to: toDate || "",
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/dues-register?${params.toString()}`,
      "dues_register.csv"
    );
  }

  async function downloadInventoryLedgerSummaryCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      q: searchText || "",
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/inventory-ledger-summary?${params.toString()}`,
      "inventory_ledger_summary.csv"
    );
  }

  // ✅ Agent Stock Availability CSV (added)
  async function downloadAgentStockAvailabilityCSV() {
    if (!agentFilter) {
      alert("Please select Agent first");
      return;
    }

    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      agent_id: String(agentFilter),
      q: searchText || "",
      include_zero: includeZero ? "1" : "0",
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/agent-stock-availability?${params.toString()}`,
      "agent_stock_availability.csv"
    );
  }

  // ✅ (5) CSV download
  async function downloadTopPerformersRollingCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      days: String(rollingDays),
      sort: topSort,
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/top-performers-rolling?${params.toString()}`,
      `top_performers_${rollingDays}d_${topSort}.csv`
    );
  }

  async function downloadHouseholdSummaryCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      from: fromDate || "",
      to: toDate || "",
      state: state || "",
      district: district || "",
      block: block || "",
      panchayat: panchayat || "",
      village: village || "",
      agent_id: selectedAgentId || "",
      is_shareholder: hhIsShareholder || "",
      is_active: hhIsActive || "",
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/households?${params.toString()}`,
      "household_onboarding_summary.csv"
    );
  }

  async function downloadFpoMappedGeographyCSV() {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      search: searchText || "",
      export: "csv",
    });

    await downloadCsvWithAuth(
      `${API_BASE}/reports/fpo-mapped-geography?${params.toString()}`,
      "fpo_mapped_geography.csv"
    );
  }

  // ✅ Added exactly as requested
  async function handleDownloadCSV() {
    if (activeReport === "sales_register") {
      await downloadSalesRegisterCSV();
      return;
    }

    if (activeReport === "agent_income_margin") {
      await downloadAgentIncomeMarginCSV();
      return;
    }

    // ✅ added (at the specific place)
    if (activeReport === "top_performers_rolling") {
      await downloadTopPerformersRollingCSV();
      return;
    }

    if (activeReport === "dues_register") {
      await downloadDuesRegisterCSV();
      return;
    }

    if (activeReport === "stock_availability") {
      await downloadStockAvailabilityCSV();
      return;
    }

    if (activeReport === "inventory_ledger_summary") {
      await downloadInventoryLedgerSummaryCSV();
      return;
    }

    // ✅ added
    if (activeReport === "agent_stock_availability") {
      await downloadAgentStockAvailabilityCSV();
      return;
    }

    if (activeReport === "expiry_tracker") {
      await downloadExpiryTrackerCSV();
      return;
    }

    if (activeReport === "hh_summary") {
      await downloadHouseholdSummaryCSV();
      return;
    }

    if (activeReport === "fpo_mapped_geography") {
      await downloadFpoMappedGeographyCSV();
      return;
    }
  }

  async function fetchSalesRegister() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),

        // Filtering (invoice_date)
        period,
        fy,
        from: fromDate || "",
        to: toDate || "",

        // Optional geo filters (optional, backend may ignore in Step-2)
        state: state || "",
        district: district || "",
        block: block || "",
        panchayat: panchayat || "",
      });

      const res = await fetch(
        `${API_BASE}/reports/sales-register?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );

      if (!res.ok) throw new Error(`API failed: ${res.status}`);

      const data = await res.json();

      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
    } catch (e) {
      setError(e.message || "Failed to load report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAgentInvoices() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        agent_id: agentFilter || "",
        from: fromDate || "",
        to: toDate || "",
      });

      const res = await fetch(
        `${API_BASE}/reports/agent-invoices?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );
      if (!res.ok) throw new Error(`API failed: ${res.status}`);

      const data = await res.json();
      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
    } catch (e) {
      setError(e.message || "Failed to load agent invoices");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDuesRegister() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        period,
        fy,
        from: fromDate || "",
        to: toDate || "",
      });

      const res = await fetch(
        `${API_BASE}/reports/dues-register?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );
      if (!res.ok) throw new Error(`API failed: ${res.status}`);

      const data = await res.json();
      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
    } catch (e) {
      setError(e.message || "Failed to load report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchStockAvailability() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        q: searchText || "",
      });

      const res = await fetch(
        `${API_BASE}/reports/stock-availability?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );
      if (!res.ok) throw new Error(`API failed: ${res.status}`);

      const data = await res.json();
      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
    } catch (e) {
      setError(e.message || "Failed to load report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchInventoryLedgerSummary() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        q: searchText || "",
      });

      const res = await fetch(
        `${API_BASE}/reports/inventory-ledger-summary?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );
      if (!res.ok) throw new Error(`API failed: ${res.status}`);

      const data = await res.json();
      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
    } catch (e) {
      setError(e.message || "Failed to load report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAgentIncomeMargin() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        period,
        fy,
        from: fromDate || "",
        to: toDate || "",
      });

      const res = await fetch(
        `${API_BASE}/reports/agent-income?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );
      if (!res.ok) throw new Error(`API failed: ${res.status}`);
      const data = await res.json();

      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
    } catch (e) {
      setError(e.message || "Failed to load report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  // ✅ Fetch report (added)
  async function fetchAgentStockAvailability() {
    setLoading(true);
    setError("");

    try {
      if (!agentFilter) {
        setColumns([]);
        setRows([]);
        setTotalRows(0);
        setReportTotals(null);
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        agent_id: String(agentFilter),
        q: searchText || "",
        include_zero: includeZero ? "1" : "0",
      });

      const res = await fetch(
        `${API_BASE}/reports/agent-stock-availability?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );
      if (!res.ok) throw new Error(`API failed: ${res.status}`);
      const data = await res.json();

      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
      setReportTotals(data.totals || null);
    } catch (e) {
      setError(e.message || "Failed to load report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
      setReportTotals(null);
    } finally {
      setLoading(false);
    }
  }

  // ✅ (3) Fetch function
  async function fetchTopPerformersRolling() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        days: String(rollingDays),
        sort: String(topSort),
      });

      const res = await fetch(
        `${API_BASE}/reports/top-performers-rolling?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error || data?.message || `API failed: ${res.status}`
        );
      }

      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
    } catch (e) {
      setError(e.message || "Failed to load report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchExpiryTracker() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        agent_id: selectedAgentId || "",
        show_zero: showZero ? "1" : "0",
      });

      const res = await fetch(
        `${API_BASE}/reports/expiry-tracker?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );
      if (!res.ok) throw new Error(`API failed: ${res.status}`);

      const data = await res.json();

      setColumns(Array.isArray(data.columns) ? data.columns : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(data.total_rows || 0);
    } catch (e) {
      setError(e.message || "Failed to load expiry tracker");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchHouseholdSummary() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        from: fromDate || "",
        to: toDate || "",
        state: state || "",
        district: district || "",
        block: block || "",
        panchayat: panchayat || "",
        village: village || "",
        agent_id: selectedAgentId || "",
        is_shareholder: hhIsShareholder || "",
        is_active: hhIsActive || "",
      });

      const res = await fetch(
        `${API_BASE}/reports/households?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || `API failed: ${res.status}`);
      }

      const reportColumns = [
        { key: "id", label: "HH ID" },
        { key: "agent_name", label: "Agent" },
        { key: "agent_phone", label: "Agent Phone" },
        { key: "household_name", label: "Household Name" },
        { key: "calling_name", label: "Calling Name" },
        { key: "phone", label: "Mobile" },
        { key: "state", label: "State" },
        { key: "district", label: "District" },
        { key: "block", label: "Block" },
        { key: "panchayat", label: "Panchayat" },
        { key: "village", label: "Village" },
        { key: "lat", label: "Latitude" },
        { key: "lng", label: "Longitude" },
        { key: "is_shareholder", label: "Shareholder" },
        { key: "folio_code", label: "Folio Code" },
        { key: "shareholder_id", label: "Shareholder ID" },
        { key: "household_source", label: "HH Source" },
        { key: "is_active", label: "Active" },
        { key: "created_at", label: "Created Date" },
      ];

      const reportRows = Array.isArray(data.data)
        ? data.data.map((r) => ({
            ...r,
            is_shareholder: r.is_shareholder ? "Yes" : "No",
            is_active: r.is_active ? "Yes" : "No",
            created_at: r.created_at
              ? new Date(r.created_at).toLocaleString()
              : "",
          }))
        : [];

      setColumns(reportColumns);
      setRows(reportRows);
      setTotalRows(data.pagination?.total || 0);
    } catch (e) {
      setError(e.message || "Failed to load household report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchFpoMappedGeography() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        search: searchText || "",
      });

      const res = await fetch(
        `${API_BASE}/reports/fpo-mapped-geography?${params.toString()}`,
        {
          headers: authHeaders(),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || `API failed: ${res.status}`);
      }

      const reportColumns = [
        { key: "fpo_name", label: "FPO" },
        { key: "state_name", label: "State" },
        { key: "state_code", label: "State Code" },
        { key: "district_name", label: "District" },
        { key: "district_code", label: "District Code" },
        { key: "district_mapped", label: "District Mapped" },
        { key: "block_name", label: "Block" },
        { key: "block_code", label: "Block Code" },
        { key: "block_mapped", label: "Block Mapped" },
        { key: "panchayat_count", label: "Panchayat Count" },
        { key: "village_count", label: "Village Count" },
        { key: "mobile_available", label: "Mobile Available" },
      ];

      const reportRows = Array.isArray(data.data)
        ? data.data.map((r) => ({
            ...r,
            district_mapped: r.district_mapped ? "Yes" : "No",
            block_mapped: r.block_mapped ? "Yes" : "No",
            mobile_available: r.block_mapped ? "Yes" : "No",
            block_name: r.block_name || "",
            block_code: r.block_code || "",
            panchayat_count: r.panchayat_count || 0,
            village_count: r.village_count || 0,
          }))
        : [];

      setColumns(reportColumns);
      setRows(reportRows);
      setTotalRows(data.pagination?.total || 0);
    } catch (e) {
      setError(e.message || "Failed to load FPO mapped geography report");
      setColumns([]);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }

  // ✅ Fetch agents for dropdown (reuse existing API)
  const [agentsList, setAgentsList] = React.useState([]);

  async function fetchAgentsList() {
    try {
      const res = await fetch(`${API_BASE}/agents`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setAgentsList(Array.isArray(data) ? data : data.agents || []);
    } catch {}
  }

  React.useEffect(() => {
    fetchAgentsList();
  }, []); // once

  React.useEffect(() => {
    if (!isSalesRegister) return;
    fetchSalesRegister();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSalesRegister,
    period,
    fy,
    fromDate,
    toDate,
    state,
    district,
    block,
    panchayat,
    page,
    pageSize,
  ]);

  React.useEffect(() => {
    if (!isAgentInvoices) return;
    fetchAgentInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgentInvoices, agentFilter, fromDate, toDate, page, pageSize]);

  React.useEffect(() => {
    if (!isDuesRegister) return;
    fetchDuesRegister();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDuesRegister, period, fy, fromDate, toDate, page, pageSize]);

  React.useEffect(() => {
    if (!isStockAvailability) return;
    fetchStockAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStockAvailability, searchText, page, pageSize]);

  React.useEffect(() => {
    if (!isInventoryLedgerSummary) return;
    fetchInventoryLedgerSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInventoryLedgerSummary, searchText, page, pageSize]);

  React.useEffect(() => {
    if (!isAgentIncomeMargin) return;
    fetchAgentIncomeMargin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAgentIncomeMargin,
    period,
    fy,
    fromDate,
    toDate,
    page,
    pageSize,
  ]);

  // ✅ Agent Stock Availability effect (added)
  React.useEffect(() => {
    if (!isAgentStockAvailability) return;
    fetchAgentStockAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAgentStockAvailability,
    agentFilter,
    includeZero,
    searchText,
    page,
    pageSize,
  ]);

  // ✅ (3) useEffect
  React.useEffect(() => {
    if (!isTopPerformersRolling) return;
    fetchTopPerformersRolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTopPerformersRolling, rollingDays, topSort, page, pageSize]);

  React.useEffect(() => {
    if (!isExpiryTracker) return;
    fetchExpiryTracker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpiryTracker, selectedAgentId, showZero, page, pageSize]);

  React.useEffect(() => {
    if (!isHouseholdSummary) return;
    fetchHouseholdSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isHouseholdSummary,
    fromDate,
    toDate,
    state,
    district,
    block,
    panchayat,
    village,
    selectedAgentId,
    hhIsShareholder,
    hhIsActive,
    page,
    pageSize,
  ]);

  React.useEffect(() => {
    if (!isFpoMappedGeography) return;
    fetchFpoMappedGeography();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFpoMappedGeography, searchText, page, pageSize]);

  // Update report selection when tab changes
  function onTabChange(tabKey) {
    setActiveTab(tabKey);
    const t = tabs.find((x) => x.key === tabKey);
    if (t && t.reports.length > 0) setActiveReport(t.reports[0].key);

    // reset paging
    setPage(1);
    setPageSize(50);
  }

  // ✅ minimal change so downloadable (added agent_stock_availability)
  const canDownloadCSV =
    isSalesRegister ||
    isDuesRegister ||
    isStockAvailability ||
    isInventoryLedgerSummary ||
    isAgentIncomeMargin ||
    isAgentStockAvailability ||
    isTopPerformersRolling ||
    isExpiryTracker ||
    isHouseholdSummary ||
    isFpoMappedGeography;

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>Reports</h2>

        {!isProcurementReport && <button
          className="btn"
          onClick={handleDownloadCSV}
          disabled={!canDownloadCSV}
          title={
            !canDownloadCSV
              ? "CSV download is available for Sales Register, Dues Register and Stock Availability"
              : activeReport === "sales_register"
              ? "Download Sales Register as CSV"
              : activeReport === "dues_register"
              ? "Download Dues Register as CSV"
              : activeReport === "stock_availability"
              ? "Download Stock Availability as CSV"
              : activeReport === "inventory_ledger_summary"
              ? "Download Inventory Ledger Summary as CSV"
              : activeReport === "agent_stock_availability"
              ? "Download Agent Stock Availability as CSV"
              : activeReport === "top_performers_rolling"
              ? "Download Top Performers (Rolling) as CSV"
              : activeReport === "expiry_tracker"
              ? "Download Expiry Report as CSV"
              : activeReport === "hh_summary"
              ? "Download Household Onboarding Summary as CSV"
              : activeReport === "fpo_mapped_geography"
              ? "Download FPO Mapped Geography as CSV"
              : "Download CSV"
          }
          style={{
            padding: "8px 12px",
            cursor: !canDownloadCSV ? "not-allowed" : "pointer",
          }}
        >
          Download CSV
        </button>}
      </div>

      {/* Top Tabs */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginTop: 12,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => onTabChange(t.key)}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #ddd",
              background: activeTab === t.key ? "#f3f3f3" : "white",
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Sub Tabs (Reports) */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginTop: 10,
        }}
      >
        {activeTabObj.reports.map((r) => (
          <button
            key={r.key}
            onClick={() => {
              setActiveReport(r.key);
              setPage(1);
              setPageSize(50);
            }}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #ddd",
              background: activeReport === r.key ? "#eaeaea" : "white",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {isProcurementReport ? (isRecordedBags ? <RecordedBags /> : <ProcurementRegister />) : <>
      {/* Filter Bar */}
      <div
        style={{
          marginTop: 14,
          padding: 12,
          border: "1px solid #e6e6e6",
          borderRadius: 12,
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12, color: "#666" }}>Period</div>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#666" }}>
              Financial Year
            </div>
            <select value={fy} onChange={(e) => setFy(e.target.value)}>
              {FY_OPTIONS.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#666" }}>From</div>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#666" }}>To</div>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#666" }}>
              State (optional)
            </div>
            <input
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="State"
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#666" }}>
              District (optional)
            </div>
            <input
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder="District"
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#666" }}>
              Block (optional)
            </div>
            <input
              value={block}
              onChange={(e) => setBlock(e.target.value)}
              placeholder="Block"
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#666" }}>
              Panchayat (optional)
            </div>
            <input
              value={panchayat}
              onChange={(e) => setPanchayat(e.target.value)}
              placeholder="Panchayat"
            />
          </div>

          {isHouseholdSummary && (
            <>
              <div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  Village (optional)
                </div>
                <input
                  value={village}
                  onChange={(e) => {
                    setVillage(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Village"
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  Agent (optional)
                </div>
                <select
                  value={selectedAgentId}
                  onChange={(e) => {
                    setSelectedAgentId(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Agents</option>
                  {agentsList.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  Shareholder HH
                </div>
                <select
                  value={hhIsShareholder}
                  onChange={(e) => {
                    setHhIsShareholder(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  Active Status
                </div>
                <select
                  value={hhIsActive}
                  onChange={(e) => {
                    setHhIsActive(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </>
          )}

          {/* ✅ (4) Add filters UI (only for this report) */}
          {isTopPerformersRolling && (
            <>
              <div>
                <label>Rolling Days</label>
                <select
                  value={rollingDays}
                  onChange={(e) => {
                    setRollingDays(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="7">Last 7 days</option>
                  <option value="14">Last 14 days</option>
                  <option value="30">Last 30 days</option>
                </select>
              </div>

              <div>
                <label>Sort By</label>
                <select
                  value={topSort}
                  onChange={(e) => {
                    setTopSort(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="sales">Sales (₹)</option>
                  <option value="paid">Paid Collected (₹)</option>
                  <option value="margin">Margin (₹)</option>
                  <option value="score">Score</option>
                </select>
              </div>
            </>
          )}

          {activeReport === "expiry_tracker" && (
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 10,
                width: "100%",
              }}
            >
              <div style={{ minWidth: 220 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: "#555",
                    marginBottom: 4,
                  }}
                >
                  Agent (optional)
                </div>
                <select
                  value={selectedAgentId}
                  onChange={(e) => {
                    setSelectedAgentId(e.target.value);
                    setPage(1);
                  }}
                  style={{ width: "100%", padding: 10 }}
                >
                  <option value="">All Agents</option>
                  {agentsList.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 22,
                }}
              >
                <input
                  type="checkbox"
                  checked={showZero}
                  onChange={(e) => {
                    setShowZero(e.target.checked);
                    setPage(1);
                  }}
                />
                <span style={{ fontSize: 13 }}>Show zero stock</span>
              </label>
            </div>
          )}

          {/* ✅ Show Agent dropdown + toggle in filter bar */}
          {(isAgentStockAvailability || isAgentInvoices) && (
            <>
              <div>
                <label>
                  Agent {isAgentStockAvailability ? "*" : "(optional)"}
                </label>
                <select
                  value={agentFilter}
                  onChange={(e) => {
                    setAgentFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">
                    {isAgentStockAvailability
                      ? "Select Agent"
                      : "All Agents"}
                  </option>
                  {agentsList.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 22,
                }}
              >
                <input
                  type="checkbox"
                  checked={includeZero}
                  onChange={(e) => {
                    setIncludeZero(e.target.checked);
                    setPage(1);
                  }}
                />
                <span>Show zero stock</span>
              </div>
            </>
          )}

          <div style={{ display: "flex", alignItems: "end" }}></div>
        </div>
      </div>

      {/* Placeholder Body */}
      <div
        style={{
          marginTop: 14,
          padding: 14,
          borderRadius: 12,
          border: "1px solid #eee",
          background: "#fff",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {activeTabObj.label} →{" "}
          {
            activeTabObj.reports.find((r) => r.key === activeReport)
              ?.label
          }
        </div>
        <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>
          P1: UI structure only. In P2/P3 we’ll connect APIs, add table +
          pagination (50/100/200/500), and enable CSV download.
        </div>

        {isSalesRegister ||
        isDuesRegister ||
        isStockAvailability ||
        isInventoryLedgerSummary ||
        isAgentIncomeMargin ||
        isAgentStockAvailability ||
        isTopPerformersRolling ||
        isExpiryTracker ||
        isAgentInvoices ||
        isHouseholdSummary ||
        isFpoMappedGeography ? (
          <div style={{ marginTop: 14 }}>
            {/* Status */}
            {error ? (
              <div
                style={{
                  padding: 10,
                  border: "1px solid #f0caca",
                  background: "#fff5f5",
                  borderRadius: 10,
                }}
              >
                {error}
              </div>
            ) : null}

            {/* Table */}
            <div
              style={{
                marginTop: 10,
                border: "1px solid #eee",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ fontSize: 13, color: "#444" }}>
                  Total: <b>{totalRows}</b>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#666" }}>Rows</div>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table className="report-table" style={{ width: "100%", ...(isAgentInvoices ? { minWidth: 1000 } : {}) }}>
                  <thead>
                    <tr>
                      {(columns.length
                        ? columns
                        : [{ key: "loading", label: "Loading..." }]
                      ).map((c) => (
                        <th key={c.key} style={c.key === "invoice_pdf_url" ? invoiceActionCellStyle : undefined}>{c.key === "invoice_pdf_url" ? "Actions" : c.label}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={Math.max(1, columns.length)}
                          style={{ padding: 12 }}
                        >
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={Math.max(1, columns.length)}
                          style={{ padding: 12 }}
                        >
                          No data
                        </td>
                      </tr>
                    ) : (
                      rows.map((r, idx) => (
                        <tr key={idx}>
                          {columns.map((c) => (
                            <td key={c.key} style={c.key === "invoice_pdf_url" ? invoiceActionCellStyle : undefined}>
                              {c.key === "invoice_pdf_url" ? (
                                <span style={{ display: "inline-flex", gap: 12, whiteSpace: "nowrap" }}>
                                  <button type="button" disabled={invoiceLoading} onClick={() => openInvoice(r)} aria-label={`View invoice ${r.invoice_no || r.sale_id}`}>👁 View</button>{" "}
                                  <button type="button" disabled={invoiceLoading} onClick={() => openInvoice(r, true)} aria-label={`Download invoice ${r.invoice_no || r.sale_id}`}>Download</button>
                                </span>
                              ) : c.key === "expiry_date" && r[c.key] ? (
                                new Date(r[c.key]).toLocaleDateString()
                              ) : (
                                r[c.key] ?? ""
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div
                style={{
                  padding: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <button
                  className="btn"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage(1)}
                >
                  {"<<"}
                </button>
                <button
                  className="btn"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => clampPage(p - 1))}
                >
                  {"<"}
                </button>

                <div style={{ fontSize: 13 }}>
                  Page <b>{page}</b> / <b>{totalPages}</b>
                </div>

                <button
                  className="btn"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => clampPage(p + 1))}
                >
                  {">"}
                </button>
                <button
                  className="btn"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage(totalPages)}
                >
                  {">>"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              border: "1px dashed #ddd",
              borderRadius: 10,
            }}
          >
            <div style={{ fontSize: 13, color: "#444" }}>
              Preview Area
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#777",
                marginTop: 6,
              }}
            >
              Table + charts will appear here (P3+)
            </div>
          </div>
        )}
      </div>
      </>}
      {invoiceLoading && <p role="status">Loading invoice…</p>}
      {invoiceError && <p role="alert">{invoiceError}</p>}
      {invoicePreview && (
        <div role="dialog" aria-modal="true" aria-label="Invoice preview" onKeyDown={event => { if (event.key === "Escape") closeInvoice(); }}
          style={{ position: "fixed", inset: "3vh 3vw", zIndex: 1000, background: "white", border: "1px solid #888", boxShadow: "0 0 0 100vmax #0008", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: 12, display: "flex", justifyContent: "space-between" }}>
            <strong>Invoice preview</strong>
            <div>
              <button type="button" onClick={() => downloadInvoiceDocument(invoicePreview)}>Download PDF</button>{" "}
              <button type="button" autoFocus onClick={closeInvoice}>Close preview</button>
            </div>
          </div>
          <iframe title="Invoice PDF" src={invoicePreview.url} style={{ width: "100%", flex: 1, border: 0 }} />
        </div>
      )}
    </div>
  );
}
